import {
  Box,
  Collapse,
  ExpandMoreRoundedIcon,
  Flex,
  FormCheckbox,
  type FormCheckboxProps,
  IconButton,
  mergeSx,
  Rotate,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { type PropsWithChildren } from '@shared/utils';
import { useController, type FieldValues } from 'react-hook-form';

export interface TopicSelectionProps<TFieldValues extends FieldValues = FieldValues>
  extends FormCheckboxProps<TFieldValues>,
    PropsWithChildren {
  endAdornment?: React.ReactNode;
}

export default function TopicSelection<TFieldValues extends FieldValues = FieldValues>(
  props: Readonly<TopicSelectionProps<TFieldValues>>,
) {
  const {
    endAdornment,
    sx,
    slotProps,
    children,
    defaultChecked: isDefaultChecked,
    ...restProps
  } = props;
  const [isOpen, collapseHandler] = useDisclosure();

  const {
    field: { value },
  } = useController({ ...props, name: props.name });

  return (
    <Box
      sx={(theme) => ({
        borderRadius: theme.shape.rounded(2),
        border: '1px solid',
        borderColor: theme.palette.grey[300],
        overflow: 'hidden',
      })}
    >
      <Flex
        sx={(theme) => ({
          p: theme.spacing(2, 4),
          gap: theme.spacing(4),
          alignItems: 'center',
          bgcolor: isOpen ? theme.palette.primary.light : 'transparent',
        })}
      >
        <FormCheckbox
          {...restProps}
          checked={isDefaultChecked ? true : value}
          sx={mergeSx(
            (theme) => ({
              color: theme.palette.grey[400],
              p: theme.spacing(0),
              '&.Mui-disabled': {
                color: theme.palette.grey[300],
              },
            }),
            sx,
          )}
          slotProps={{
            formControlLabel: {
              ...slotProps?.formControlLabel,
              sx: mergeSx(
                (theme) => ({
                  gap: theme.spacing(4),
                  flex: 1,
                }),
                slotProps?.formControlLabel?.sx,
              ),
            },
            typography: {
              variant: 'body1Semibold',
              ...slotProps?.typography,
              sx: {
                '&.MuiFormControlLabel-label.Mui-disabled': {
                  color: 'inherit',
                },
              },
            },
          }}
        />
        {endAdornment}
        <IconButton onClick={collapseHandler.toggle}>
          <Rotate in={isOpen}>
            <ExpandMoreRoundedIcon sx={{ color: (theme) => theme.palette.grey[600] }} />
          </Rotate>
        </IconButton>
      </Flex>
      <Collapse in={isOpen}>{children}</Collapse>
    </Box>
  );
}
